/**
 * VaultPickerModal — fetches the user's Vault docs and lets them attach
 * one or more to the active chat session.
 *
 * Calls `GET /api/v2/vault/documents` with the user's bearer token. Empty
 * vaults render a CTA that routes to `/chat/vault`.
 */
import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Search, FolderOpen, FileText, X } from 'lucide-react';

import { useAuth } from '@/contexts/AuthContext';
import { useLanguage } from '@/contexts/LanguageContext';
import { getBackendBaseUrl } from '@/lib/backendUrl';
import { cn } from '@/lib/utils';

const BACKEND_URL = getBackendBaseUrl();

export function VaultPickerModal({ open, onClose, onConfirm }) {
  const navigate = useNavigate();
  const { language } = useLanguage();
  const { session } = useAuth();
  const isArabic = language === 'ar';

  const [docs, setDocs]         = useState([]);
  const [loading, setLoading]   = useState(false);
  const [query, setQuery]       = useState('');
  const [selected, setSelected] = useState(new Set());

  useEffect(() => {
    if (!open || !session?.access_token) return;
    setLoading(true);
    fetch(`${BACKEND_URL}/api/vault/documents`, {
      headers: { Authorization: `Bearer ${session.access_token}` },
    })
      .then((r) => (r.ok ? r.json() : { documents: [] }))
      .then((data) => {
        const raw = Array.isArray(data?.documents) ? data.documents : [];
        // Normalise to {id, name, type, content_preview} regardless of
        // whether the backend returned vault-shape (filename / file_type)
        // or picker-shape (name / type).
        setDocs(raw.map((d) => ({
          id: d.id,
          name: d.name || d.filename || 'Untitled',
          type: (d.type || d.file_type || '').toUpperCase(),
          content_preview: d.content_preview || d.content_text || d.text || '',
        })));
      })
      .catch(() => setDocs([]))
      .finally(() => setLoading(false));
  }, [open, session?.access_token]);

  const filtered = useMemo(() => {
    if (!query.trim()) return docs;
    const q = query.trim().toLowerCase();
    return docs.filter((d) => (d.name || '').toLowerCase().includes(q));
  }, [docs, query]);

  if (!open) return null;

  const toggle = (id) => {
    setSelected((cur) => {
      const next = new Set(cur);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });
  };

  const confirm = () => {
    const picked = docs.filter((d) => selected.has(d.id));
    onConfirm(picked.map((d) => ({
      id: d.id,
      name: d.name,
      content: d.content_preview || d.text || '',
    })));
    onClose();
    setSelected(new Set());
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center"
      style={{ background: 'rgba(0,0,0,0.45)' }}
      onClick={onClose}
      data-testid="vault-picker-modal"
    >
      <div
        dir={isArabic ? 'rtl' : 'ltr'}
        onClick={(e) => e.stopPropagation()}
        style={{
          background: '#FFFFFF',
          border: '1px solid #E8E8E8',
          borderRadius: '16px',
          width: '560px',
          maxHeight: '480px',
          boxShadow: '0 20px 60px rgba(0,0,0,0.10)',
          display: 'flex', flexDirection: 'column',
        }}
      >
        <div className="px-5 pt-4 pb-3 border-b" style={{ borderColor: '#F0F0F0' }}>
          <div className="flex items-center justify-between mb-2">
            <h2 className="text-[15px] font-medium font-arabic" style={{ color: '#111111' }}>
              {isArabic ? 'اختر من الخزانة القانونية' : 'Pick from your Vault'}
            </h2>
            <button onClick={onClose} aria-label="Close" data-testid="vault-picker-close" style={{ color: '#999999' }}>
              <X className="w-4 h-4" />
            </button>
          </div>
          <div className="flex items-center gap-2 px-2 py-1 rounded-md" style={{ background: '#F5F5F3' }}>
            <Search className="w-3.5 h-3.5" style={{ color: '#999999' }} />
            <input
              type="text"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder={isArabic ? 'ابحث في الخزانة...' : 'Search the vault...'}
              className="flex-1 bg-transparent outline-none text-[13px] font-arabic"
              data-testid="vault-picker-search"
              style={{ color: '#111111' }}
            />
          </div>
        </div>

        <div className="flex-1 overflow-y-auto" data-testid="vault-picker-list">
          {loading && (
            <div className="px-5 py-6 text-[12px]" style={{ color: '#999999' }}>{isArabic ? 'جاري التحميل...' : 'Loading...'}</div>
          )}
          {!loading && filtered.length === 0 && (
            <div className="px-5 py-10 text-center">
              <FolderOpen className="w-10 h-10 mx-auto mb-2" strokeWidth={1.4} style={{ color: '#999999' }} />
              <div className="text-[13px] font-medium font-arabic" style={{ color: '#111111' }}>
                {isArabic ? 'الخزانة فارغة' : 'Vault is empty'}
              </div>
              <div className="text-[11px] mt-1 font-arabic" style={{ color: '#666666' }}>
                {isArabic ? 'ارفع مستنداتك في الخزانة القانونية لتظهر هنا.' : 'Upload documents to your Vault and they will appear here.'}
              </div>
              <button
                onClick={() => { onClose(); navigate('/chat/vault'); }}
                className="mt-3 inline-flex items-center px-3 py-1.5 text-[12px] font-arabic"
                style={{ background: '#111111', color: '#FFFFFF', borderRadius: '9999px' }}
                data-testid="vault-picker-go-vault"
              >
                {isArabic ? 'الذهاب إلى الخزانة' : 'Go to the Vault'}
              </button>
            </div>
          )}
          {!loading && filtered.map((d) => {
            const isOn = selected.has(d.id);
            return (
              <button
                key={d.id}
                type="button"
                onClick={() => toggle(d.id)}
                className={cn('w-full px-5 flex items-center gap-3 hover:bg-[#F7F7F5]', isArabic && 'flex-row-reverse text-right')}
                style={{ height: '44px', borderBottom: '1px solid #F0F0F0' }}
                data-testid={`vault-picker-row-${d.id}`}
              >
                <span
                  aria-hidden="true"
                  className="flex items-center justify-center"
                  style={{
                    width: '16px', height: '16px',
                    border: '1px solid #E0E0E0',
                    borderRadius: '3px',
                    background: isOn ? '#111111' : '#FFFFFF',
                  }}
                >
                  {isOn && <span style={{ width: 4, height: 8, borderRight: '2px solid #FFF', borderBottom: '2px solid #FFF', transform: 'rotate(45deg) translate(0, -1px)' }} />}
                </span>
                <FileText className="w-3.5 h-3.5" style={{ color: '#666666' }} />
                <span className="flex-1 truncate font-arabic" style={{ fontSize: '13px', color: '#111111' }}>
                  {d.name || (isArabic ? 'بدون اسم' : 'Untitled')}
                </span>
                <span style={{ fontSize: '11px', color: '#999999' }}>{d.type || ''}</span>
              </button>
            );
          })}
        </div>

        <div className={cn('px-5 py-3 flex items-center justify-between border-t', isArabic && 'flex-row-reverse')} style={{ borderColor: '#F0F0F0' }}>
          <span className="text-[12px] font-arabic" style={{ color: '#666666' }}>
            {isArabic ? `${selected.size} مستند محدد` : `${selected.size} selected`}
          </span>
          <div className={cn('flex items-center gap-2', isArabic && 'flex-row-reverse')}>
            <button onClick={onClose} className="px-3 py-1.5 text-[12px] font-arabic" style={{ background: '#FFFFFF', border: '1px solid #E8E8E8', color: '#111111', borderRadius: '9999px' }} data-testid="vault-picker-cancel">
              {isArabic ? 'إلغاء' : 'Cancel'}
            </button>
            <button onClick={confirm} disabled={selected.size === 0} className="px-3 py-1.5 text-[12px] font-arabic" style={{ background: selected.size ? '#111111' : '#E8E8E8', color: selected.size ? '#FFFFFF' : '#999999', borderRadius: '9999px' }} data-testid="vault-picker-confirm">
              {isArabic ? 'إضافة للمحادثة' : 'Attach to chat'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

export default VaultPickerModal;
